/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import {
	CosmosButton,
	CosmosRichText,
	CosmosTable,
	CosmosTagProps,
	CosmosText,
	CosmosTextVariantEnum,
	TextLink,
} from "@components/elements/atoms";
import { TagList } from "@components/elements/molecules";
import { CosmosPluginDownloadDetailFragment, PluginListItemFragment } from "@cosmos/hygraph-schema";
import { useMessages } from "@epic-mw/i18n";
import { css } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";
import { formatDate, formatFileSize } from "../blob-table/BlobTable.utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Select } from "@eds-web/react";
import type { Option } from "@eds-web/react";

type DownloadOption<T extends string> = Option<T> & {
	os?: string;
};

export interface PluginListTableItemProps extends PluginListItemFragment {
	showUpdatedDate?: boolean;
	showSize?: boolean;
	tags?: CosmosTagProps[];
	osName?: string;
}

const selectStyle = css({ width: "195px", maxW: "195px" });

export const PluginListTableItem = ({
	title,
	desc,
	learnMoreUrl,
	downloads,
	extraButtons,
	osName,
	showUpdatedDate = true,
	showSize = true,
	tags = [],
	id,
}: PluginListTableItemProps): React.JSX.Element | null => {
	const messages = useMessages();
	const [latestDownload, setLatestDownload] = useState<CosmosPluginDownloadDetailFragment | null>(
		downloads?.length ? downloads[0] : null,
	);

	const downloadMap = useMemo(() => {
		const map = new Map<string, CosmosPluginDownloadDetailFragment>();
		downloads.forEach((d) => {
			if (d.downloadUrl) {
				map.set(d.downloadUrl, d);
			}
		});
		return map;
	}, [downloads]);

	const validLabels: DownloadOption<string>[] = useMemo(
		() =>
			downloads
				.filter((d) => d.downloadUrl)
				?.map((d) => ({
					label: `${d.infoContent} ${messages["epic.cosmos.os." + d.operatingSystem]}`,
					value: d.downloadUrl || "",
					os: d.operatingSystem?.toLowerCase(),
				})),
		[downloads, messages],
	);

	const findBestOption = useCallback(
		(targetOs?: string): Option<string> | undefined => {
			if (!validLabels.length) return undefined;

			if (targetOs) {
				const match = validLabels.find((label) => label.os === targetOs);
				if (match) return match;
			}

			const windowsMatch = validLabels.find((label) => label.os === "windows");
			if (windowsMatch) return windowsMatch;

			return validLabels[0];
		},
		[validLabels],
	);

	const [selected, setSelected] = useState<DownloadOption<string> | undefined>(() => findBestOption(osName));

	useEffect(() => {
		const matchedOption = findBestOption(osName);

		if (matchedOption) {
			setSelected(matchedOption);
			const download = downloadMap.get(matchedOption.value);
			if (download) setLatestDownload(download);
		}
	}, [osName, findBestOption, downloadMap]);

	const onChange = useCallback(
		(option: Option<string>) => {
			setSelected(option);
			const download = downloadMap.get(option.value);
			if (download) setLatestDownload(download);
		},
		[downloadMap],
	);

	if (!title) return null;

	// Build the description line with links
	const descParts: React.ReactNode[] = [];
	if (desc) {
		descParts.push(
			<CosmosRichText
				variant="paragraphSM"
				color="eds.text.secondary"
				html={desc}
				key={`list-table-item-desc-${id}`}
			/>,
		);
	}

	if (latestDownload?.tpsNoticesUrl) {
		if (descParts.length > 0) {
			descParts.push(<span key={`list-table-item-sep1-${id}`}> - </span>);
		}
		descParts.push(
			<TextLink
				key={`list-table-item-tps-${id}`}
				href={latestDownload.tpsNoticesUrl}
				label={messages["epic.cosmos.plugins.tpsNotices"] || "TPS Notices"}
				variant={CosmosTextVariantEnum.paragraphSM}
			/>,
		);
	}

	if (latestDownload?.releaseNotesUrl) {
		if (descParts.length > 0) {
			descParts.push(<span key={`list-table-item-sep2-${id}`}> - </span>);
		}
		descParts.push(
			<TextLink
				key={`list-table-item-notes-${id}`}
				href={latestDownload.releaseNotesUrl}
				label={messages["epic.cosmos.plugins.releaseNotes"] || "Release Notes"}
				variant={CosmosTextVariantEnum.paragraphSM}
			/>,
		);
	}

	return (
		<CosmosTable.Row w="full">
			<CosmosTable.Cell gap="eds.dimension.8" alignSelf="center">
				<VStack gap="eds.dimension.8" alignItems="flex-start">
					<CosmosText variant={CosmosTextVariantEnum.paragraphLGBold} color="eds.text.primary" text={title} />
					{descParts.length > 0 ? (
						<div
							className={css({
								textStyle: "paragraphSM",
								color: "eds.text.secondary",
								"& a": {
									display: "inline-block",
									textDecoration: "underline",
								},
							})}
						>
							{descParts}
						</div>
					) : null}
					{tags.length > 0 ? <TagList tags={tags} size="uiSM" /> : null}
				</VStack>
			</CosmosTable.Cell>
			{showSize ? (
				<CosmosTable.Cell alignSelf="center">
					<CosmosText variant="uiMD" color="eds.text.primary" text={formatFileSize(latestDownload?.sizeInBytes || 0)} />
				</CosmosTable.Cell>
			) : null}
			{showUpdatedDate ? (
				<CosmosTable.Cell alignSelf="center">
					<CosmosText
						variant="uiMD"
						color="eds.text.primary"
						text={formatDate(latestDownload?.dateLastUpdated || "")}
					/>
				</CosmosTable.Cell>
			) : null}
			<CosmosTable.Cell
				alignItems="center"
				justifyContent="flex-end"
				alignSelf="center"
				className={css({
					display: "flex",
				})}
			>
				<HStack gap="eds.dimension.12">
					{selected && validLabels?.length > 1 ? (
						<Select
							aria-label={messages["epic.cosmos.os"]}
							clearable={false}
							value={selected}
							placeholder={messages["epic.cosmos.os"]}
							options={validLabels}
							removeLabel={"true"}
							label=""
							onChange={onChange}
							className={selectStyle}
						></Select>
					) : null}
					{latestDownload?.downloadUrl ? (
						<CosmosButton
							href={latestDownload.downloadUrl}
							label={messages["epic.cosmos.plugins.download"]}
							variant="secondary"
							size="lg"
							name={title}
						/>
					) : null}
					{learnMoreUrl ? (
						<CosmosButton
							href={learnMoreUrl}
							label={messages["epic.cosmos.plugins.learnMore"] || "Learn More"}
							variant="outline"
							size="lg"
							name={`${title} Learn More`}
						/>
					) : null}
					{extraButtons?.map((button, index) => (
						<CosmosButton
							key={`list-table-item-${button.label}-${index}`}
							icon={button.icon}
							label={button.label}
							variant={button.variant}
							href={button.href}
							iconPosition={button.iconPosition}
							name={button.name || button.label}
							size="lg"
						/>
					))}
				</HStack>
			</CosmosTable.Cell>
		</CosmosTable.Row>
	);
};
